import { observable, computed, action } from 'mobx'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { memoize } from '@/utils/memoize-util'
import { type NotificationStore } from '@/features/notifications/NotificationStore'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { NotificationListViewModel } from '@/features/notifications/NotificationListViewModel'
import type { GetCurrentUserWorkspaceMembershipsFn } from '@/features/iam'
import type { JobFn } from '@/features/jobs'
import type { GetTimeFn } from '@/features/notifications'
import type {
  AlertErrorHandlerFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'

/**
 * Notifications sidebar view model.
 */
export class NotificationsSidebarViewModel {
  /**
   * The workspace to filter by. `Workspace.none` shows notifications for all
   * workspaces.
   */
  @observable private accessor _internalSelectedWorkspace = Workspace.none

  readonly modal = new ModalViewModel()

  /**
   * One list per workspace, including `Workspace.none`, so each list stays
   * loaded when switching back to it.
   */
  private readonly _listFor = memoize(
    (workspace: Workspace) =>
      new NotificationListViewModel(
        workspace,
        this._notificationStore,
        this._getTime,
        // When a notification is interacted with (clicked), hide the sidebar.
        this.modal.close,
        this._job,
        this._createAlertErrorHandler,
        this._createAlertRetryWrapper,
      ),
  )

  constructor(
    private readonly _notificationStore: NotificationStore,
    private readonly _getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    private readonly _getTime: GetTimeFn,
    private readonly _job: JobFn,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {}

  /**
   * The notification list view model.
   */
  @computed
  get list() {
    return this._listFor(this.selectedWorkspace)
  }

  /**
   * Available workspaces.
   */
  @computed
  get workspaces() {
    return this._getCurrentUserWorkspaceMemberships().map((m) => m.workspace)
  }

  /**
   * The workspace being filtered on, uses the first one if there's only one available.
   */
  @computed
  get selectedWorkspace(): Workspace {
    return this.workspaces.length === 1
      ? this.workspaces[0]
      : this._internalSelectedWorkspace
  }

  /**
   * Selects a workspace.
   */
  @action.bound
  selectWorkspace(workspace: Workspace) {
    this._internalSelectedWorkspace = workspace
  }

  /**
   * Shows or hides the sidebar.
   */
  @action.bound
  toggle() {
    if (this.modal.showing) {
      this.modal.close()
    } else {
      this.modal.show()
    }
  }
}
